<template>
  <div class="dash">
    <!-- Header -->
    <div class="hero">
      <div class="hero__left">
        <div class="hero__kicker">Incentives</div>
        <h2 class="hero__title">
          Welcome, <span class="hero__name">{{ user.name }}</span>
        </h2>

        <div class="hero__meta">
          <span class="chip">
            Role: <b>{{ user.role }}</b>
          </span>
          <span class="chip">
            Branch: <b>{{ user.branchId }}</b>
          </span>
          <span class="chip chip--muted">
            Month: <b>{{ monthKey }}</b>
          </span>
        </div>
      </div>

      <div class="hero__right">
        <button class="btn btn--ghost" @click="syncMonth" :disabled="loading">
          <span v-if="loading" class="spin"></span>
          {{ loading ? "Syncing…" : "Sync now" }}
        </button>

        <router-link
          v-if="user.role==='super_admin'"
          class="btn btn--primary"
          to="/sales/new"
        >
          + Add Sale
        </router-link>
      </div>
    </div>

    <!-- Alerts -->
    <div v-if="error" class="alert alert--danger">
      {{ error }}
    </div>

    <!-- Manager note -->
    <div v-if="user.role==='branch_manager'" class="callout">
      <div class="callout__icon">📌</div>
      <div class="callout__body">
        <div class="callout__title">Manager Note</div>
        <div class="callout__text">
          Your monthly target is <b>₹60,00,000</b>. Branch incentives apply only if the branch
          achieves at least <b>80%</b> of target in the month.
        </div>
      </div>
    </div>

    <!-- Progress -->
    <div class="card">
      <div class="card__head">
        <div>
          <div class="card__title">Current Month Progress</div>
          <div class="card__sub">
            {{ targetSub }} • Incentive rate adjusts with achievement
          </div>
        </div>

        <div class="card__right">
          <span class="pill" :class="gate ? 'pill--ok' : 'pill--warn'">
            <span class="dot"></span>
            {{ gate ? "Incentives Unlocked" : "Not Unlocked" }}
          </span>
        </div>
      </div>

      <div class="progressWrap">
        <progress-bar :pct="achPct"></progress-bar>

        <div class="progressMeta">
          <div class="progressMeta__left">
            <div class="mono big">₹ {{ fmt(totalSales) }}</div>
            <div class="muted">This month sales</div>
          </div>

          <div class="progressMeta__right">
            <div class="mono big">{{ achPct.toFixed(1) }}%</div>
            <div class="muted">Achievement</div>
          </div>
        </div>
      </div>

      <div class="statsGrid">
        <stat-card title="This Month Sales" :sub="targetSub">
          {{ fmt(totalSales) }}
        </stat-card>

        <stat-card title="Achievement">
          {{ achPct.toFixed(1) }}%
        </stat-card>

        <stat-card title="Monthly Incentive Rate">
          {{ (monthRate*100).toFixed(2) }}%
        </stat-card>

        <stat-card title="Benefits Unlock">
          <template v-if="gate">
            <span class="badge badge--ok">Unlocked ✅</span>
          </template>
          <template v-else>
            <span class="warn">
              You're {{ neededPct.toFixed(1) }}% (₹{{ fmt(neededAmt) }}) away from unlocking incentives.
            </span>
          </template>
        </stat-card>
      </div>
    </div>

    <!-- Incentives totals -->
    <div class="card">
      <div class="card__head">
        <div>
          <div class="card__title">Incentives</div>
          <div class="card__sub">This month breakdown</div>
        </div>

        <div class="card__right">
          <span class="pill pill--neutral">
            <span class="dot"></span>
            Grand Total: <b style="margin-left:6px;">₹ {{ fmt(grandTotal) }}</b>
          </span>
        </div>
      </div>

      <div class="statsGrid">
        <stat-card title="Discount Incentive">
          {{ fmt(discountIncent) }}
        </stat-card>

        <stat-card title="Ticket Size Incentive">
          {{ fmt(ticketIncent) }}
        </stat-card>

        <stat-card title="Monthly Target Incentive">
          {{ fmt(monthlyIncent) }}
        </stat-card>

        <stat-card title="Grand Total">
          {{ fmt(grandTotal) }}
        </stat-card>
      </div>
    </div>

    <!-- Recent sales -->
    <div class="card">
      <div class="card__head">
        <div>
          <div class="card__title">Recent Sales</div>
          <div class="card__sub">This month entries</div>
        </div>
      </div>

      <div class="tableWrap">
        <sales-table :rows="monthSales"></sales-table>
      </div>
    </div>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import { mapGetters } from 'vuex'
import ProgressBar from '../components/ProgressBar.vue'
import StatCard from '../components/StatCard.vue'
import SalesTable from '../components/SalesTable.vue'
import {
  formatINR,
  computeEmployeeMonthSummary,
  computeManagerMonthSummary,
  TARGET_EMPLOYEE,
  TARGET_MANAGER
} from '../utils/incentives'

export default {
  components: { ProgressBar, StatCard, SalesTable },
  data () {
    return {
      loading: false,
      error: '',
      expenseAmt: 0
    }
  },
  computed: {
    ...mapGetters('auth', ['currentUser']),
    user () {
      return this.currentUser || { id:'', name:'', role:'employee', branchId:'' }
    },
    monthKey () { return dayjs().format('YYYY-MM') },

    monthSales () {
      if (!this.user || !this.user.id) return []
      const monthKey = this.monthKey
      if (this.user.role === 'branch_manager') {
        const g = this.$store.getters['sales/byBranchMonth']
        return typeof g === 'function' ? g({ branchId: this.user.branchId, monthKey }) : []
      }
      const f = this.$store.getters['sales/byUserMonth']
      return typeof f === 'function' ? f({ userId: this.user.id, monthKey }) : []
    },

    summary () {
      if (this.user.role === 'branch_manager') {
        return computeManagerMonthSummary(this.monthSales, this.expenseAmt || 0)
      }
      return computeEmployeeMonthSummary(this.monthSales)
    },

    totalSales () { return this.summary.totalSales || 0 },
    achPct () { return Math.min(100, this.summary.achPct || 0) },
    monthRate () { return this.summary.monthRate || 0 },
    gate () { return !!this.summary.gate },
    discountIncent () { return this.summary.discountIncent || 0 },
    ticketIncent () { return this.summary.ticketIncent || 0 },
    monthlyIncent () { return this.summary.monthlyIncent || 0 },
    expenseBonus () { return this.summary.expenseBonus || 0 },
    grandTotal () { return this.summary.grandTotal || 0 },

    targetSub () {
      var t = this.user.role === 'branch_manager' ? TARGET_MANAGER : TARGET_EMPLOYEE
      return 'Target: ₹' + (t/100000).toFixed(0) + ',00,000'
    },

    target () {
      if (this.user && this.user.target) return this.user.target
      return this.user.role === 'branch_manager' ? 60 * 100000 : 10 * 100000
    },

    neededAmt () {
      var left = Math.max(0, 0.8 * this.target - this.totalSales)
      return left
    },
    neededPct () {
      var left = Math.max(0, 80 - (this.summary.achPct || 0))
      return left
    }
  },

  methods: {
    fmt: formatINR,

    async syncMonth () {
      if (!this.user || !this.user.id) return
      this.loading = true; this.error = ''
      try {
        // 1) Pull sales for the month
        var r = await this.$api.get('/sales', { params: { month: this.monthKey } })
        var list = (r && r.data && Array.isArray(r.data)) ? r.data : []
        this.$store.commit('sales/SET_SALES_FROM_ORDERS', list)

        // 2) Manager branch expense
        if (this.user.role === 'branch_manager' && this.user.branchId) {
          var x = await this.$api.get('/expenses/' + this.user.branchId + '/' + this.monthKey)
          var amt = (x && x.data && typeof x.data.amount !== 'undefined') ? x.data.amount : 0
          this.expenseAmt = Number(amt || 0)
        } else {
          this.expenseAmt = 0
        }
      } catch (e) {
        this.error = (e && e.message) ? e.message : 'Failed to sync month data'
      } finally {
        this.loading = false
      }
    }
  },

  async mounted () {
    await this.syncMonth()
  },

  watch: {
    user () { this.syncMonth() }
  }
}
</script>


<style scoped>
.dash{
  padding: 14px;
  display:flex;
  flex-direction:column;
  gap: 14px;
}

/* Hero */
.hero{
  background: linear-gradient(135deg, rgba(182, 40, 40, 0.95), rgba(43, 95, 207, 0.78));
  color:#fff;
  border-radius: 18px;
  padding: 16px;
  box-shadow: 0 16px 45px rgba(0,0,0,.10);
  border: 1px solid rgba(255,255,255,.10);
  display:flex;
  justify-content:space-between;
  gap: 14px;
  flex-wrap: wrap;
}
.hero__kicker{
  font-size: 12px;
  letter-spacing: .08em;
  text-transform: uppercase;
  opacity: .8;
  font-weight: 800;
}
.hero__title{
  margin: 4px 0 0 0;
  font-size: 22px;
  font-weight: 950;
}
.hero__name{ opacity: .95; }
.hero__meta{
  margin-top: 10px;
  display:flex;
  gap: 8px;
  flex-wrap: wrap;
}
.chip{
  background: rgba(255,255,255,.10);
  border: 1px solid rgba(255,255,255,.16);
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
}
.chip--muted{ opacity:.9; }
.hero__right{
  display:flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
}

/* Cards */
.card{
  background:#fff;
  border: 1px solid rgba(0,0,0,.08);
  border-radius: 16px;
  padding: 14px;
  box-shadow: 0 10px 30px rgba(0,0,0,.04);
}
.card__head{
  display:flex;
  justify-content:space-between;
  align-items:flex-start;
  gap: 10px;
  flex-wrap: wrap;
}
.card__title{
  font-weight: 950;
  letter-spacing: .2px;
  font-size: 15px;
}
.card__sub{
  margin-top: 4px;
  opacity: .7;
  font-weight: 700;
  font-size: 12px;
}
.card__right{
  display:flex;
  align-items:center;
  gap: 10px;
}

/* Callout */
.callout{
  background: rgba(59,130,246,.08);
  border: 1px solid rgba(59,130,246,.18);
  border-radius: 16px;
  padding: 12px 14px;
  display:flex;
  gap: 12px;
  align-items:flex-start;
}
.callout__icon{
  width: 34px; height: 34px;
  border-radius: 12px;
  display:flex;
  align-items:center;
  justify-content:center;
  background: rgba(59,130,246,.15);
}
.callout__title{
  font-weight: 950;
  margin-bottom: 4px;
}
.callout__text{
  opacity: .85;
  font-weight: 700;
  line-height: 1.35;
}

/* Progress section */
.progressWrap{ margin-top: 10px; }
.progressMeta{
  margin-top: 10px;
  display:flex;
  justify-content:space-between;
  gap: 10px;
  flex-wrap: wrap;
  padding: 10px 12px;
  border-radius: 14px;
  background: rgba(0,0,0,.02);
  border: 1px solid rgba(0,0,0,.06);
}
.big{ font-size: 18px; font-weight: 950; }
.muted{ opacity: .7; font-weight: 700; font-size: 12px; }
.mono{
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}

/* Stats grid */
.statsGrid{
  margin-top: 12px;
  display:grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
}
@media (max-width: 980px){
  .statsGrid{ grid-template-columns: 1fr; }
}

/* Pills / badges */
.pill{
  display:inline-flex;
  align-items:center;
  gap: 8px;
  padding: 7px 10px;
  border-radius: 999px;
  border: 1px solid rgba(0,0,0,.10);
  background: rgba(0,0,0,.03);
  font-size: 12px;
  font-weight: 900;
}
.dot{ width: 10px; height: 10px; border-radius: 99px; background:#9ca3af; }
.pill--ok{ background: rgba(16,185,129,.10); border-color: rgba(16,185,129,.22); color:#065f46; }
.pill--ok .dot{ background:#10b981; }
.pill--warn{ background: rgba(245,158,11,.10); border-color: rgba(245,158,11,.22); color:#92400e; }
.pill--warn .dot{ background:#f59e0b; }
.pill--neutral{ background: rgba(156,163,175,.10); border-color: rgba(156,163,175,.22); color:#374151; }

.badge{
  display:inline-flex;
  align-items:center;
  padding: 6px 10px;
  border-radius: 999px;
  font-weight: 950;
  font-size: 12px;
  border: 1px solid rgba(0,0,0,.10);
}
.badge--ok{
  background: rgba(16,185,129,.10);
  border-color: rgba(16,185,129,.22);
  color:#065f46;
}
.warn{ color:#b45309; font-weight: 800; }

/* Buttons */
.btn{
  border: 1px solid rgba(0,0,0,.12);
  background:#fff;
  color:#111827;
  padding: 9px 12px;
  border-radius: 12px;
  font-weight: 900;
  cursor:pointer;
  display:inline-flex;
  align-items:center;
  gap: 8px;
}
.btn:disabled{ opacity:.55; cursor:not-allowed; }
.btn--primary{
  background:#fff;
  color:#111827;
}
.btn--ghost{
  background: rgba(255,255,255,.10);
  border-color: rgba(255,255,255,.18);
  color:#fff;
}

/* Loading spinner dot */
.spin{
  width: 12px; height: 12px;
  border-radius: 99px;
  border: 2px solid rgba(255,255,255,.40);
  border-top-color: rgba(255,255,255,1);
  display:inline-block;
  animation: sp 0.9s linear infinite;
}
@keyframes sp { to { transform: rotate(360deg); } }

/* Alert */
.alert{
  padding: 10px 12px;
  border-radius: 14px;
  font-weight: 800;
  border: 1px solid rgba(0,0,0,.10);
}
.alert--danger{
  background: rgba(239,68,68,.08);
  border-color: rgba(239,68,68,.18);
  color:#991b1b;
}

/* Table wrapper (gives breathing room if table is wide) */
.tableWrap{
  margin-top: 10px;
  overflow:auto;
  border-radius: 14px;
}
</style>